import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import QuotationService from "../../services/quotation.service";

// ================= COMMON ERROR HANDLER =================
const handleError = (error, thunkAPI) =>
  thunkAPI.rejectWithValue(
    error?.response?.data?.message || error.message || "Something went wrong"
  );

// ================= THUNKS =================

export const generateQuotation = createAsyncThunk(
  "quotation/generateQuotation",
  async (data, thunkAPI) => {
    try {
      const res = await QuotationService.generateQuotation(data);
      return res.data;
    } catch (error) {
      return handleError(error, thunkAPI);
    }
  }
);

export const getQuotation = createAsyncThunk(
  "quotation/getQuotation",
  async (item, thunkAPI) => {
    try {
      const res = await QuotationService.getQuotation(item);
      return res.data;
    } catch (error) {
      return handleError(error, thunkAPI);
    }
  }
);

export const sendQuotation = createAsyncThunk(
  "quotation/sendQuotation",
  async (data, thunkAPI) => {
    try {
      const res = await QuotationService.sendQuotation(data);
      return res.data;
    } catch (error) {
      return handleError(error, thunkAPI);
    }
  }
);

export const editQuotation = createAsyncThunk(
  "quotation/editQuotation",
  async (data, thunkAPI) => {
    try {
      const res = await QuotationService.editQuotation(data);
      return res.data;
    } catch (error) {
      return handleError(error, thunkAPI);
    }
  }
);

export const deleteQuotation = createAsyncThunk(
  "quotation/deleteQuotation",
  async (id, thunkAPI) => {
    try {
      await QuotationService.deleteQuotation(id);
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);


export const resendQuotation = createAsyncThunk(
  "quotation/resendQuotation",
  async (data, thunkAPI) => {
    try {
      const res = await QuotationService.resendQuotation(data);
      return res.data;
    } catch (error) {
      return handleError(error, thunkAPI);
    }
  }
);

export const recalculateQuotation = createAsyncThunk(
  "quotation/recalculateQuotation",
  async (data, thunkAPI) => {
    try {
      const res = await QuotationService.recalculateQuotation(data);
      return res.data;
    } catch (error) {
      return handleError(error, thunkAPI);
    }
  }
);

export const updateHotelQuotation = createAsyncThunk(
  "quotation/updateHotelQuotation",
  async (data, thunkAPI) => {
    try {
      const res = await QuotationService.updateHotelQuotation(data);
      return res.data;
    } catch (error) {
      return handleError(error, thunkAPI);
    }
  }
);

export const updateVehicleQuotation = createAsyncThunk(
  "quotation/updateVehicleQuotation",
  async (data, thunkAPI) => {
    try {
      const res = await QuotationService.updateVehicleQuotation(data);
      return res.data;
    } catch (error) {
      return handleError(error, thunkAPI);
    }
  }
);

export const updateFerryQuotation = createAsyncThunk(
  "quotation/updateFerryQuotation",
  async (data, thunkAPI) => {
    try {
      const res = await QuotationService.updateFerryQuotation(data);
      return res.data;
    } catch (error) {
      return handleError(error, thunkAPI);
    }
  }
);

export const addCustomerPayment = createAsyncThunk(
  "quotation/addCustomerPayment",
  async (data, thunkAPI) => {
    try {
      const res = await QuotationService.addCustomerPayment(data);
      return res.data;
    } catch (error) {
      return handleError(error, thunkAPI);
    }
  }
);

export const addVendorPayment = createAsyncThunk(
  "quotation/addVendorPayment",
  async (data, thunkAPI) => {
    try {
      const res = await QuotationService.addVendorPayment(data);
      return res.data;
    } catch (error) {
      return handleError(error, thunkAPI);
    }
  }
);

// ================= INITIAL STATE =================

const initialState = {
  loading: false,
  error: null,
  quotationList: [],
  calculation: null,
  updateSuccess: false,
};

// ================= SLICE =================

const quotationSlice = createSlice({
  name: "quotation",
  initialState,

  reducers: {
    resetUpdateStatus: (state) => {
      state.updateSuccess = false;
    },
  },

  extraReducers: (builder) => {
    builder

      // ===== GENERATE =====
      .addCase(generateQuotation.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload?.data || action.payload;

        if (data) {
          state.quotationList.unshift(data);
          state.calculation = data.calculation || null;
        }

        state.updateSuccess = true;
      })

      // ===== GET =====
      .addCase(getQuotation.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload?.data || action.payload;
        state.quotationList = Array.isArray(data) ? data : [data];
      })

      // ===== RECALCULATE =====
      .addCase(recalculateQuotation.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload?.data || action.payload;
        state.calculation = data?.calculation || null;
      })

      // ===== DELETE =====
.addCase(
  deleteQuotation.fulfilled,
  (state, action) => {

    state.loading = false;

    state.quotationList =
      state.quotationList.filter(
        (item) =>
          item._id !== action.payload
      );

    state.updateSuccess = true;
  }
)


      // ===== SEND / RESEND =====
      .addMatcher(
        (action) =>
          ["quotation/sendQuotation/fulfilled", "quotation/resendQuotation/fulfilled"].includes(action.type),
        (state) => {
          state.loading = false;
          state.updateSuccess = true;
        }
      )

      // ===== UPDATE (HOTEL / VEHICLE / FERRY) =====
      .addMatcher(
        (action) =>
          action.type.startsWith("quotation/update") &&
          action.type.endsWith("/fulfilled"),
        (state, action) => {
          state.loading = false;
          const data = action.payload?.data || action.payload;
          state.calculation = data?.calculation || null;
          state.updateSuccess = true;
        }
      )

      // ===== PAYMENTS =====
      .addMatcher(
        (action) =>
          action.type.includes("Payment") &&
          action.type.endsWith("/fulfilled"),
        (state) => {
          state.loading = false;
          state.updateSuccess = true;
        }
      )

      // ===== GLOBAL PENDING =====
      .addMatcher(
        (action) =>
          action.type.startsWith("quotation/") &&
          action.type.endsWith("/pending"),
        (state) => {
          state.loading = true;
          state.error = null;
          state.updateSuccess = false;
        }
      )

      // ===== GLOBAL ERROR =====
      .addMatcher(
        (action) =>
          action.type.startsWith("quotation/") &&
          action.type.endsWith("/rejected"),
        (state, action) => {
          state.loading = false;
          state.error = action.payload;
        }
      );
  },
});

export const { resetUpdateStatus } = quotationSlice.actions;
export default quotationSlice.reducer;